export interface SourceData {
  utm_source?: string; // facebook, google, twitter, tiktok, etc.
  utm_medium?: string; // cpc, organic, social, video, etc.
  utm_campaign?: string; // campaign name/identifier
  utm_content?: string; // ad creative identifier
  utm_term?: string; // keyword/audience targeting
  firstVisit: string; // timestamp of first visit
  referrer?: string; // HTTP referrer header
  landingPage?: string; // first page visited
  userAgent?: string; // browser/device info
  walletType?: 'embedded' | 'external'; // wallet connection type
  embeddedWallet?: {
    email?: string;
    username?: string;
    authProvider: 'google' | 'apple' | 'facebook' | 'x' | 'discord' | 'farcaster' | 'github' | 'email';
    accountType: 'eoa' | 'smartAccount';
  };
}

const SOURCE_STORAGE_KEY = "deathx_source_data";
const SOURCE_EXPIRY_DAYS = 30; // Attribution window

export class SourceTracker {
  /**
   * Captures source data from URL parameters and browser context
   */
  static captureSourceData(): SourceData | null {
    try {
      // Check if we already have source data stored (within attribution window)
      const existing = this.getStoredSourceData();
      if (existing) {
        return existing;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const now = new Date().toISOString();

      // Extract UTM parameters
      const sourceData: SourceData = {
        utm_source: urlParams.get("utm_source") || undefined,
        utm_medium: urlParams.get("utm_medium") || undefined,
        utm_campaign: urlParams.get("utm_campaign") || undefined,
        utm_content: urlParams.get("utm_content") || undefined,
        utm_term: urlParams.get("utm_term") || undefined,
        firstVisit: now,
        referrer: document.referrer || undefined,
        landingPage: window.location.pathname + window.location.search,
        userAgent: navigator.userAgent,
      };

      // Only store if we have meaningful source data
      const hasUtmData =
        sourceData.utm_source ||
        sourceData.utm_medium ||
        sourceData.utm_campaign;
      const hasReferrer =
        sourceData.referrer && !this.isInternalReferrer(sourceData.referrer);

      if (hasUtmData || hasReferrer) {
        this.storeSourceData(sourceData);
        return sourceData;
      }

      // For organic traffic, store minimal data
      const organicData: SourceData = {
        utm_medium: "organic",
        firstVisit: now,
        landingPage: window.location.pathname + window.location.search,
        userAgent: navigator.userAgent,
      };

      this.storeSourceData(organicData);
      return organicData;
    } catch (error) {
      console.error("Error capturing source data:", error);
      return null;
    }
  }

  /**
   * Retrieves stored source data if within attribution window
   */
  static getStoredSourceData(): SourceData | null {
    try {
      const stored = localStorage.getItem(SOURCE_STORAGE_KEY);
      if (!stored) return null;

      const data = JSON.parse(stored);
      const firstVisit = new Date(data.firstVisit);
      const now = new Date();
      const daysDiff =
        (now.getTime() - firstVisit.getTime()) / (1000 * 60 * 60 * 24);

      // Check if data is within attribution window
      if (daysDiff <= SOURCE_EXPIRY_DAYS) {
        return data;
      } else {
        // Data expired, remove it
        localStorage.removeItem(SOURCE_STORAGE_KEY);
        return null;
      }
    } catch (error) {
      console.error("Error retrieving stored source data:", error);
      return null;
    }
  }

  /**
   * Stores source data in localStorage
   */
  private static storeSourceData(data: SourceData): void {
    try {
      localStorage.setItem(SOURCE_STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error("Error storing source data:", error);
    }
  }

  /**
   * Checks if referrer is from the same domain (internal)
   */
  private static isInternalReferrer(referrer: string): boolean {
    try {
      const referrerUrl = new URL(referrer);
      const currentHost = window.location.hostname;
      return (
        referrerUrl.hostname === currentHost ||
        referrerUrl.hostname === `www.${currentHost}`
      );
    } catch {
      return false;
    }
  }

  /**
   * Clears stored source data (useful for testing or user privacy)
   */
  static clearSourceData(): void {
    localStorage.removeItem(SOURCE_STORAGE_KEY);
  }

  /**
   * Gets source data for API submission (returns current stored data)
   */
  static getSourceDataForAPI(): SourceData | null {
    return this.getStoredSourceData();
  }

  /**
   * Tracks Google Analytics custom event with source data
   */
  static trackSourceEvent(eventName: string, sourceData?: SourceData): void {
    try {
      const data = sourceData || this.getStoredSourceData();
      if (!data) return;

      // Send custom event to Google Analytics if gtag is available
      if (typeof window.gtag === "function") {
        window.gtag("event", eventName, {
          custom_parameter_utm_source: data.utm_source,
          custom_parameter_utm_medium: data.utm_medium,
          custom_parameter_utm_campaign: data.utm_campaign,
          custom_parameter_utm_content: data.utm_content,
          custom_parameter_utm_term: data.utm_term,
        });
      }
    } catch (error) {
      console.error("Error tracking source event:", error);
    }
  }

  /**
   * Initialize source tracking on page load
   */
  static initialize(): SourceData | null {
    const sourceData = this.captureSourceData();

    // Track page view with source data
    if (sourceData) {
      this.trackSourceEvent("page_view_with_source", sourceData);
    }

    return sourceData;
  }
}

// Declare gtag for TypeScript
declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
  }
}
